import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import type {
  BatchDeclarationGate,
  ExternalToolBatch,
  NestedToolCallTimeouts,
  ToolCallExpectedGates,
} from './external-tool-batch.types';
import {
  deleteAliasedExternalToolBatches,
  getBatchAliasIds,
  getLiveBatchAliasId,
  getReadyToolCallIdsForFlush,
  getWorkToken,
  isBatchReady,
  isBatchWorkCurrent,
  isToolCallIdKnownToBatch,
  resolveSharedBatchFlushKey,
} from './external-tool-batch-gates';
import {
  aliasChatIdToWaitingBatch,
  findKnownChatBatch,
  releaseChatIdOccupancy,
} from './external-tool-batch-open';
import { syncLostExecuteForBatch } from './external-tool-lost-execute';
import {
  buildExternalToolResultsMessage,
  getFlushResults,
  globalCompletedExternalToolResultsByBatchAndToolCallId,
  hasRecentlyFlushedResult,
  dropAlreadyDeliveredBatchResults,
  isBatchEmpty,
  wasExternalToolResultSuccessfullyDelivered,
  markSuccessfullyDeliveredResults,
  rememberRecentlyFlushedResults,
  removeFlushedResultsFromBatch,
  reportBackgroundFlushError,
} from './external-tool-result-cache';

export function createExternalToolFlushController({
  batches,
  inFlightFlushes,
  inFlightHandlerCounts,
  priorFlushByChatId,
  lastReportedActivityByChatId,
  workTokenByChatId,
  handlerWorkTokenByBatchId,
  lostExecuteTimers,
  batchDeclarationGates,
  toolCallExpectedGates,
  sendMessage,
  onChatActivityChange,
  onFlushSettled,
}: {
  batches: Map<string, ExternalToolBatch>;
  inFlightFlushes: Map<string, Promise<void>>;
  inFlightHandlerCounts: Map<string, number>;
  priorFlushByChatId: Map<string, Promise<void>>;
  lastReportedActivityByChatId: Map<string, boolean>;
  workTokenByChatId: Map<string, number>;
  handlerWorkTokenByBatchId: Map<string, number>;
  lostExecuteTimers: NestedToolCallTimeouts;
  batchDeclarationGates: Map<string, BatchDeclarationGate>;
  toolCallExpectedGates: ToolCallExpectedGates;
  sendMessage: (chatId: string, message: TerraUIMessage) => Promise<void>;
  onChatActivityChange?: (params: { chatId: string; active: boolean }) => void;
  onFlushSettled?: (params: {
    chatId: string;
    skipBatch: ExternalToolBatch | undefined;
  }) => void;
}) {
  const getInFlightHandlerCount = (batchId: string): number =>
    inFlightHandlerCounts.get(batchId) ?? 0;

  const getBatchInFlightHandlerCount = (batchId: string): number =>
    getBatchAliasIds({ batches, batchId }).reduce(
      (count, aliasId) => count + getInFlightHandlerCount(aliasId),
      0,
    );

  const isChatActive = (chatId: string): boolean => {
    for (const [batchId, batch] of batches) {
      if (batch.chatId !== chatId) {
        continue;
      }

      if (
        inFlightFlushes.has(batchId) ||
        getInFlightHandlerCount(batchId) > 0 ||
        batch.pendingExecuteToolCallIds.size > 0 ||
        batch.lostExecuteToolCallIds.size > 0 ||
        batch.requests.size > 0 ||
        batch.results.size > 0
      ) {
        return true;
      }
    }

    return false;
  };

  const recomputeChatActivity = (chatId: string): void => {
    if (!onChatActivityChange) {
      return;
    }

    const active = isChatActive(chatId);

    if ((lastReportedActivityByChatId.get(chatId) ?? false) === active) {
      return;
    }

    lastReportedActivityByChatId.set(chatId, active);
    onChatActivityChange({ chatId, active });
  };

  const trackChatFlush = (
    chatId: string,
    flushPromise: Promise<void>,
  ): void => {
    const prior = priorFlushByChatId.get(chatId) ?? Promise.resolve();
    const chained = prior
      .then(() => flushPromise)
      .catch((error: unknown) => {
        console.warn(
          '[ExternalToolResultCoordinator] Prior flush failed before chaining next flush',
          {
            error: error instanceof Error ? error : new Error(String(error)),
          },
        );
      });

    priorFlushByChatId.set(chatId, chained);
    recomputeChatActivity(chatId);
    void chained.finally(() => {
      if (priorFlushByChatId.get(chatId) === chained) {
        priorFlushByChatId.delete(chatId);
      }

      recomputeChatActivity(chatId);
    });
  };

  const tryFlushBatch = async (batchId: string): Promise<void> => {
    const flushKey = resolveSharedBatchFlushKey({ batches, batchId });
    const pendingFlush = inFlightFlushes.get(flushKey);

    if (pendingFlush) {
      await pendingFlush;
    }

    let keepFlushing = true;
    let failedSendAttempts = 0;

    while (keepFlushing) {
      keepFlushing = false;
      const batch = batches.get(flushKey);
      const inFlightHandlerCount = getBatchInFlightHandlerCount(flushKey);

      if (!batch) {
        return;
      }

      dropAlreadyDeliveredBatchResults({ batch, batchId });

      if (isBatchEmpty(batch)) {
        const flushedChatId = batch.chatId;

        deleteAliasedExternalToolBatches({
          batches,
          batch,
          batchDeclarationGates,
          toolCallExpectedGates,
        });
        aliasChatIdToWaitingBatch({ batches, chatId: flushedChatId });
        recomputeChatActivity(flushedChatId);

        return;
      }

      const batchReady = isBatchReady({
        batch,
        batchId,
        inFlightHandlerCount,
        hasRecentlyFlushedResult,
        wasDelivered: wasExternalToolResultSuccessfullyDelivered,
      });

      syncLostExecuteForBatch({
        batches,
        lostExecuteTimers,
        batchId: flushKey,
      });

      if (!batchReady) {
        return;
      }

      const toolCallIdsToFlush = getReadyToolCallIdsForFlush({
        batch,
        batchId,
        inFlightHandlerCount,
        hasRecentlyFlushedResult,
        wasDelivered: wasExternalToolResultSuccessfullyDelivered,
      });
      const orderedResults = getFlushResults({
        batch,
        batchId,
        completedResultsByToolCallId:
          globalCompletedExternalToolResultsByBatchAndToolCallId,
        toolCallIdsToFlush,
      });

      if (orderedResults.length === 0) {
        return;
      }

      const flushWorkToken = batch.workToken;
      const flushChatId = batch.chatId;

      if (getWorkToken(workTokenByChatId, flushChatId) !== flushWorkToken) {
        return;
      }

      if (inFlightFlushes.has(flushKey)) {
        await inFlightFlushes.get(flushKey);
        keepFlushing = true;
        continue;
      }

      if (getWorkToken(workTokenByChatId, flushChatId) !== flushWorkToken) {
        return;
      }

      const flushPromise = sendMessage(
        flushChatId,
        buildExternalToolResultsMessage({
          batchId: batch.flushBatchId,
          results: orderedResults,
        }),
      );

      trackChatFlush(flushChatId, flushPromise);
      inFlightFlushes.set(flushKey, flushPromise);

      try {
        await flushPromise;
        failedSendAttempts = 0;

        if (getWorkToken(workTokenByChatId, flushChatId) !== flushWorkToken) {
          return;
        }

        const batchAfterFlush = batches.get(flushKey);

        if (!batchAfterFlush || batchAfterFlush.workToken !== flushWorkToken) {
          return;
        }

        removeFlushedResultsFromBatch({
          batch: batchAfterFlush,
          batchId,
          flushedResults: orderedResults,
        });
        rememberRecentlyFlushedResults(orderedResults);
        markSuccessfullyDeliveredResults(orderedResults);

        if (isBatchEmpty(batchAfterFlush)) {
          const flushedChatId = batchAfterFlush.chatId;

          deleteAliasedExternalToolBatches({
            batches,
            batch: batchAfterFlush,
            batchDeclarationGates,
            toolCallExpectedGates,
          });
          aliasChatIdToWaitingBatch({ batches, chatId: flushedChatId });
          recomputeChatActivity(flushedChatId);

          return;
        }

        recomputeChatActivity(batchAfterFlush.chatId);
        keepFlushing = isBatchReady({
          batch: batchAfterFlush,
          batchId,
          inFlightHandlerCount: getBatchInFlightHandlerCount(flushKey),
          hasRecentlyFlushedResult,
          wasDelivered: wasExternalToolResultSuccessfullyDelivered,
        });
      } catch (error: unknown) {
        failedSendAttempts += 1;
        reportBackgroundFlushError(error);

        if (failedSendAttempts < 2) {
          keepFlushing = true;
          continue;
        }

        releaseChatIdOccupancy({ batches, batch });
        recomputeChatActivity(batch.chatId);
      } finally {
        inFlightFlushes.delete(flushKey);
        onFlushSettled?.({
          chatId: flushChatId,
          skipBatch: batch,
        });
      }
    }
  };

  const resolveHandlerBatchId = ({
    batchId,
    chatId,
    toolCallId,
  }: {
    batchId: string;
    chatId?: string;
    toolCallId?: string;
  }): { liveBatchId: string; batch: ExternalToolBatch | undefined } => {
    const known =
      chatId && toolCallId
        ? findKnownChatBatch({
            batches,
            workTokenByChatId,
            chatId,
            toolCallId,
          })
        : undefined;
    const byId = batches.get(batchId);
    const byIdUsable =
      byId &&
      isBatchWorkCurrent({ batch: byId, workTokenByChatId }) &&
      (toolCallId === undefined ||
        isToolCallIdKnownToBatch({ batch: byId, toolCallId }))
        ? byId
        : undefined;
    const batch = known ?? byIdUsable;

    return {
      liveBatchId: batch
        ? (getLiveBatchAliasId({ batches, batch }) ?? batchId)
        : batchId,
      batch,
    };
  };

  const notifyHandlerStart = ({
    batchId,
    chatId,
    toolCallId,
  }: {
    batchId: string;
    chatId?: string;
    toolCallId?: string;
  }): void => {
    const { liveBatchId, batch } = resolveHandlerBatchId({
      batchId,
      chatId,
      toolCallId,
    });

    if (!batch) {
      return;
    }

    handlerWorkTokenByBatchId.set(liveBatchId, batch.workToken);
    const nextCount = getInFlightHandlerCount(liveBatchId) + 1;

    inFlightHandlerCounts.set(liveBatchId, nextCount);
    recomputeChatActivity(batch.chatId);
  };

  const notifyHandlerEnd = ({
    batchId,
    chatId,
    toolCallId,
  }: {
    batchId: string;
    chatId?: string;
    toolCallId?: string;
  }): void => {
    const { liveBatchId, batch } = resolveHandlerBatchId({
      batchId,
      chatId,
      toolCallId,
    });

    if (!batch) {
      return;
    }

    const handlerToken = handlerWorkTokenByBatchId.get(liveBatchId);
    const currentToken = getWorkToken(workTokenByChatId, batch.chatId);

    handlerWorkTokenByBatchId.delete(liveBatchId);
    const currentCount = getInFlightHandlerCount(liveBatchId);
    const nextCount = Math.max(0, currentCount - 1);

    if (nextCount === 0) {
      inFlightHandlerCounts.delete(liveBatchId);
    } else {
      inFlightHandlerCounts.set(liveBatchId, nextCount);
    }

    if (
      currentToken === undefined ||
      (handlerToken !== undefined && handlerToken !== currentToken)
    ) {
      return;
    }

    recomputeChatActivity(batch.chatId);
    void tryFlushBatch(liveBatchId).catch(reportBackgroundFlushError);
  };

  const awaitPriorFlush = async (chatId: string): Promise<void> => {
    const priorFlush = priorFlushByChatId.get(chatId);

    if (!priorFlush) {
      return;
    }

    await priorFlush;
  };

  return {
    recomputeChatActivity,
    tryFlushBatch,
    notifyHandlerStart,
    notifyHandlerEnd,
    awaitPriorFlush,
  };
}
